import { keymap } from "prosemirror-keymap";
import { history, undo, redo } from "prosemirror-history";
import { gapCursor, GapCursor } from "prosemirror-gapcursor";
import { Decoration, DecorationSet } from "prosemirror-view";
import type { EditorView } from "prosemirror-view";
import { chainCommands, toggleMark, setBlockType, wrapIn, newlineInCode, createParagraphNear, liftEmptyBlock, splitBlock, deleteSelection, joinBackward, selectNodeBackward } from "prosemirror-commands";
import { wrappingInputRule, textblockTypeInputRule, InputRule, inputRules } from "prosemirror-inputrules";
import { Plugin, Selection } from "prosemirror-state";
import { splitListItem, sinkListItem, liftListItem } from "prosemirror-schema-list";
import type { Command, EditorState, Transaction } from "prosemirror-state";
import type { Node as PmNode } from "prosemirror-model";
import { schema } from "./schema";
import { cardLinkSuggestPlugin } from "./card-link-suggest";
import { linkAutodetectPlugin } from "./link-autodetect";
import { linkPastePlugin } from "./link-paste";
import { clipboardTextPlugin } from "./clipboard";
import { extractPlugin } from "./extract";
import { enterSection, sectionOf } from "./sections";
import { titleLinePlugin } from "./title-line";

// These block atoms are not node-selectable (see schema). A gap cursor is next
// to them, and the commands below delete and highlight them.
const NON_SELECTABLE_BLOCKS = new Set(["view_block", "card_embed"]);

type Edge = "left" | "right" | "up" | "down";

// Gap-cursor delete. prosemirror-gapcursor binds only the arrow keys, so
// Backspace/Delete at a gap cursor need defining. A gap cursor sits *between*
// two blocks and belongs to neither, so:
//   - if the block on the delete side is a view/section atom, remove it;
//   - if it's a textblock, step into it (the gap cursor isn't "attached" to the
//     text, so we move rather than destroy a paragraph).
// Backspace acts on the block before the gap; Delete on the block after.
function gapDelete(forward: boolean) {
  return (state: EditorState, dispatch?: (tr: Transaction) => void): boolean => {
    const sel = state.selection;
    if (!(sel instanceof GapCursor)) return false;
    const node = forward ? sel.$head.nodeAfter : sel.$head.nodeBefore;
    if (!node) return false;
    if (NON_SELECTABLE_BLOCKS.has(node.type.name)) {
      if (dispatch) {
        const start = forward ? sel.$head.pos : sel.$head.pos - node.nodeSize;
        dispatch(state.tr.delete(start, start + node.nodeSize).scrollIntoView());
      }
      return true;
    }
    if (dispatch) {
      const into = Selection.near(state.doc.resolve(sel.$head.pos), forward ? 1 : -1);
      dispatch(state.tr.setSelection(into).scrollIntoView());
    }
    return true;
  };
}

// Backspace and Delete at the edge of a textblock that is directly against a
// view or a section move the caret to the gap cursor between them. They do not
// join the textblock into the atom or delete the atom. If the textblock is
// empty, the command deletes it, and the atom stays. Backspace acts at the
// start edge, and Delete acts at the end edge.
function collapseToGapAtom(forward: boolean) {
  return (state: EditorState, dispatch?: (tr: Transaction) => void): boolean => {
    const { $from, empty } = state.selection;
    if (!empty || $from.depth === 0) return false;
    const atEdge = forward
      ? $from.parentOffset === $from.parent.content.size
      : $from.parentOffset === 0;
    if (!atEdge) return false;
    const boundary = forward ? $from.after() : $from.before();
    const atom = forward
      ? state.doc.resolve(boundary).nodeAfter
      : state.doc.resolve(boundary).nodeBefore;
    if (!atom || !NON_SELECTABLE_BLOCKS.has(atom.type.name)) return false;
    if (dispatch) {
      const tr = state.tr;
      if ($from.parent.content.size === 0) {
        // Drop the empty block; the gap then sits where it began (against the atom).
        const start = $from.before();
        tr.delete(start, start + $from.parent.nodeSize);
        tr.setSelection(new GapCursor(tr.doc.resolve(start)));
      } else {
        tr.setSelection(new GapCursor(tr.doc.resolve(boundary)));
      }
      dispatch(tr.scrollIntoView());
    }
    return true;
  };
}

// Stop the caret at a gap cursor on each side of a non-selectable block atom.
// The default gap cursor shows only where no other caret position exists, so
// the caret would go across the atom in one step. The stop shows where Enter
// puts the next paragraph. From the gap, the next arrow key goes into an open
// section, or across the atom.
function gapStopAroundAtom(forward: boolean, edge: Edge) {
  return (
    state: EditorState,
    dispatch?: (tr: Transaction) => void,
    view?: EditorView
  ): boolean => {
    const sel = state.selection;
    // Already at a gap cursor next to the atom: step across to the far gap.
    if (sel instanceof GapCursor) {
      const node = forward ? sel.$head.nodeAfter : sel.$head.nodeBefore;
      if (!node || !NON_SELECTABLE_BLOCKS.has(node.type.name)) return false;
      const target = forward ? sel.$head.pos + node.nodeSize : sel.$head.pos - node.nodeSize;
      if (view && dispatch && node.type.name === "card_embed") {
        if (enterSection(view, Math.min(sel.$head.pos, target), forward)) return true;
      }
      if (dispatch) {
        dispatch(state.tr.setSelection(new GapCursor(state.doc.resolve(target))).scrollIntoView());
      }
      return true;
    }
    // Leaving the edge of a textblock toward an adjacent atom: land in the gap.
    if (!sel.empty || sel.$head.depth === 0) return false;
    if (!view || !view.endOfTextblock(edge)) return false;
    const boundary = forward ? sel.$head.after() : sel.$head.before();
    const $b = state.doc.resolve(boundary);
    const node = forward ? $b.nodeAfter : $b.nodeBefore;
    if (!node || !NON_SELECTABLE_BLOCKS.has(node.type.name)) return false;
    if (dispatch) dispatch(state.tr.setSelection(new GapCursor($b)).scrollIntoView());
    return true;
  };
}

// Move the caret from the edge of a section's editor to the outer editor. The
// caret leaves at the first or the last caret position of the document.
function leaveSection(forward: boolean, edge: Edge) {
  return (
    state: EditorState,
    dispatch?: (tr: Transaction) => void,
    view?: EditorView
  ): boolean => {
    const section = view && sectionOf(view);
    if (!view || !section || !atDocumentEdge(state, view, forward, edge)) return false;
    if (dispatch) section.exit(forward);
    return true;
  };
}

// A gap cursor at the start or the end of the document has nowhere to go, so
// the key does nothing. Without this, the browser moves the hidden DOM caret of
// the gap cursor (Firefox moves it to the line above the gap), and the editor
// reads that position.
function holdGapAtEdge(forward: boolean) {
  return (state: EditorState): boolean => {
    const sel = state.selection;
    return sel instanceof GapCursor && nothingPast(state.doc, sel.$head.pos, forward);
  };
}

function atDocumentEdge(state: EditorState, view: EditorView, forward: boolean, edge: Edge): boolean {
  const sel = state.selection;
  if (!sel.empty) return false;
  if (sel instanceof GapCursor) return nothingPast(state.doc, sel.$head.pos, forward);
  if (sel.$head.depth === 0 || !view.endOfTextblock(edge)) return false;
  return nothingPast(state.doc, forward ? sel.$head.after() : sel.$head.before(), forward);
}

// True when no textblock or atom is past `boundary` in the direction.
function nothingPast(doc: PmNode, boundary: number, forward: boolean): boolean {
  let past = false;
  doc.nodesBetween(forward ? boundary : 0, forward ? doc.content.size : boundary, (node, pos) => {
    if (past) return false;
    if (!node.isTextblock && !node.isAtom) return true;
    past = forward ? pos >= boundary : pos + node.nodeSize <= boundary;
    return false;
  });
  return !past;
}

function arrow(forward: boolean, edge: Edge): Command {
  return chainCommands(gapStopAroundAtom(forward, edge), leaveSection(forward, edge), holdGapAtEdge(forward));
}

// ── Keymap ────────────────────────────────────────────────────────────────────

export const keymapPlugin = keymap({
  "Mod-b": toggleMark(schema.marks["bold"]!),
  "Mod-i": toggleMark(schema.marks["italic"]!),
  "Mod-`": toggleMark(schema.marks["code"]!),
  "Mod-z": undo,
  "Mod-y": redo,
  "Mod-Shift-z": redo,
  Enter: splitListItemOrInsertParagraph,
  Tab: sinkListItem(schema.nodes["list_item"]!),
  "Shift-Tab": liftListItem(schema.nodes["list_item"]!),
  Backspace: (state, dispatch, view) =>
    gapDelete(false)(state, dispatch) ||
    collapseToGapAtom(false)(state, dispatch) ||
    reduceListNestingOnBackspace(state, dispatch) ||
    mergeEmptyParagraphIntoList(state, dispatch) ||
    deleteSelection(state, dispatch) ||
    joinBackward(state, dispatch, view) ||
    selectNodeBackward(state, dispatch, view),
  Delete: (state, dispatch) =>
    gapDelete(true)(state, dispatch) || collapseToGapAtom(true)(state, dispatch),
  ArrowRight: arrow(true, "right"),
  ArrowLeft: arrow(false, "left"),
  ArrowDown: arrow(true, "down"),
  ArrowUp: arrow(false, "up"),
});

// Selection feedback for the non-selectable block atoms (views, sections):
//  - highlight one when a range selection covers it (they can't be
//    node-selected, so otherwise there'd be no "selected" cue);
//  - hide the native caret while a gap cursor is active so only its bar shows.
// Collapsed selections (incl. gap cursors) add no highlight, so "caret after a
// view" stays unhighlighted.
export const blockSelectionPlugin = new Plugin({
  props: {
    // While a gap cursor is active, hide the browser's native text caret (which
    // it still parks nearby) so only the gap-cursor bar shows.
    attributes(state) {
      return state.selection instanceof GapCursor ? { class: "gapcursor-active" } : {};
    },
    decorations(state) {
      const { from, to } = state.selection;
      if (from === to) return null;
      const decos: Decoration[] = [];
      state.doc.nodesBetween(from, to, (node, pos) => {
        if (NON_SELECTABLE_BLOCKS.has(node.type.name) && pos >= from && pos + node.nodeSize <= to) {
          decos.push(Decoration.node(pos, pos + node.nodeSize, { class: "block-selected" }));
        }
        return true;
      });
      return decos.length ? DecorationSet.create(state.doc, decos) : null;
    },
  },
});

// Backspace in an empty top-level paragraph that directly follows a list: drop
// the cursor at the end of the last list item instead of letting joinBackward
// re-wrap the paragraph into a fresh empty list item (which would make
// un-listing a trailing item un-undoable — backspace would just bounce back in).
function mergeEmptyParagraphIntoList(
  state: EditorState,
  dispatch?: (tr: Transaction) => void
): boolean {
  const { $from, empty } = state.selection;
  if (!empty || $from.depth !== 1 || $from.parentOffset !== 0) return false;
  if ($from.parent.type !== schema.nodes["paragraph"] || $from.parent.content.size !== 0) {
    return false;
  }
  const index = $from.index(0);
  if (index === 0 || $from.node(0).child(index - 1).type !== schema.nodes["list"]) {
    return false;
  }
  if (dispatch) {
    const paraStart = $from.before(1);
    const tr = state.tr.delete(paraStart, paraStart + $from.parent.nodeSize);
    // After deletion the list's end sits at paraStart; nudge inside it.
    tr.setSelection(Selection.near(tr.doc.resolve(paraStart), -1));
    dispatch(tr.scrollIntoView());
  }
  return true;
}

// Backspace at the start of an empty list item lifts it one level: nested items
// un-nest, top-level items leave the list entirely (become a plain paragraph).
// Gated to an empty item at cursor-start so ordinary backspace is untouched.
function reduceListNestingOnBackspace(
  state: EditorState,
  dispatch?: (tr: Transaction) => void
): boolean {
  const { $from, empty } = state.selection;
  if (!empty || $from.parentOffset !== 0 || $from.parent.content.size !== 0) {
    return false;
  }
  if ($from.node(-1).type !== schema.nodes["list_item"]) return false;
  return liftListItem(schema.nodes["list_item"]!)(state, dispatch);
}

function splitListItemOrInsertParagraph(
  state: import("prosemirror-state").EditorState,
  dispatch?: (tr: import("prosemirror-state").Transaction) => void
): boolean {
  return splitListItem(schema.nodes["list_item"]!)(state, dispatch)
    || newlineInCode(state, dispatch)
    || createParagraphNear(state, dispatch)
    || liftEmptyBlock(state, dispatch)
    || splitBlock(state, dispatch);
}

// ── Input rules ───────────────────────────────────────────────────────────────


export const inputRulesPluginReal = inputRules({
  rules: [
    // Headings
    textblockTypeInputRule(/^(#{1,4})\s$/, schema.nodes["heading"]!, (match) => ({
      level: match[1]!.length,
    })),
    // Bullet list
    wrappingInputRule(/^\s*([-*])\s$/, schema.nodes["list"]!, { style: "bullet" }),
    // Ordered list
    wrappingInputRule(/^(\d+)\.\s$/, schema.nodes["list"]!, (match) => ({
      style: "ordered",
      order: Number(match[1]),
    })),
    // Todo list: "[] " or "[ ] "
    wrappingInputRule(/^\[\s?\]\s$/, schema.nodes["list"]!, { style: "todo" }),
    // Code block
    textblockTypeInputRule(/^```$/, schema.nodes["code_block"]!),
    // Card link [[
    new InputRule(/\[\[([^\]]*)\]\]$/, (state, match, start, end) => {
      const uuid = match[1]!.trim();
      if (!uuid) return null;
      const tr = state.tr.replaceWith(
        start,
        end,
        schema.node("card_link", { uuid })
      );
      return tr;
    }),
  ],
});

export function buildPlugins(opts: { suggest?: boolean } = {}): Plugin[] {
  const { suggest = true } = opts;
  const plugins: Plugin[] = [history(), clipboardTextPlugin()];
  // Suggest plugin must precede the keymap so it can intercept Enter/Arrow
  // keys while the dropdown is open (otherwise Enter splits the block).
  // Disabled in read-only previews — no typing, and it would spawn stray DOM.
  if (suggest) plugins.push(cardLinkSuggestPlugin());
  plugins.push(keymapPlugin, inputRulesPluginReal as Plugin);
  // Only an editable editor gets the link behaviours and the extraction.
  if (suggest) plugins.push(linkAutodetectPlugin(), linkPastePlugin(), extractPlugin);
  // Visible caret in spots a normal one can't render — before/after/between
  // non-editable block atoms (view blocks), e.g. a view at the very end.
  plugins.push(gapCursor());
  // Highlight views/sections that fall inside a range selection.
  plugins.push(blockSelectionPlugin);
  // Mark the title block, which a collapsed section shows.
  plugins.push(titleLinePlugin);
  return plugins;
}
